// src/services/bedsService.js

import apiClient from "./apiClient";
import { createMissingTables } from "./subscriptionService";
import { getOrganizationId } from "../utils/organizationUtils";

const API_URL = `/beds`; // Use relative path since apiClient has baseURL 

const isMissingTableError = (error) => {
  const message = error?.response?.data?.message || error?.message || "";
  return (
    error?.response?.status === 500 &&
    (message.includes("does not exist") ||
      message.includes("from table") ||
      message.includes("Failed to"))
  );
};

export const addBed = async (bedData) => {
  // Remove organizationId from bedData if it exists (apiClient will add it as query param)
  const { organizationId, ...bedDataWithoutOrgId } = bedData;
  
  // apiClient will automatically add organizationId as query parameter via interceptor
  // (not in body, as Bed DTO doesn't accept it in request body)
  try {
    const response = await apiClient.post(`${API_URL}/create`, bedDataWithoutOrgId);
    return response.data;
  } catch (error) {
    // If org-specific tables are missing, create them once and retry.
    if (isMissingTableError(error)) {
      const orgId = getOrganizationId();
      if (orgId) {
        await createMissingTables(orgId);
        const retryResponse = await apiClient.post(`${API_URL}/create`, bedDataWithoutOrgId);
        return retryResponse.data;
      }
    }
    throw error;
  }
};

export const getAllBeds = async () => {
  try {
    // apiClient will automatically add organizationId as query param via interceptor
    // If user has no organizationId, backend should use base table
    const response = await apiClient.get(`${API_URL}/all`);
    return response.data;
  } catch (error) {
    // Handle case where organization-specific table doesn't exist yet or backend error
    if (error.response?.status === 500 && 
        (error.response?.data?.message?.includes("Failed to retrieve") ||
         error.response?.data?.message?.includes("from table") ||
         error.response?.data?.message?.includes("does not exist"))) {
      console.warn("Organization-specific table not found, returning empty array:", error.response.data.message);
      return []; // Return empty array if table doesn't exist
    }
    console.error("Error fetching beds:", error);
    throw error;
  }
};

export const getAvailableBedsByWard = async (id) => {
  // apiClient will automatically add organizationId as query param via interceptor
  const response = await apiClient.get(`${API_URL}/ward/${id}/available`);
  return response.data;
};

export const getBedsByWard = async (wardId) => {
  // apiClient will automatically add organizationId as query param via interceptor
  const response = await apiClient.get(`${API_URL}/by-ward/${wardId}`);
  return response.data;
};

export const deleteBed = async (id) => {
  const response = await apiClient.delete(`${API_URL}/${id}`);
  return response.data;
};

export const updateBed = async (id, bedData) => {
  // Remove organizationId from bedData if it exists (apiClient will add it as query param)
  const { organizationId, ...bedDataWithoutOrgId } = bedData;

  // When callers send only { isOccupied } with PUT, backend can overwrite
  // other fields (e.g. bedPrice) to defaults. Hydrate missing fields first.
  const payloadKeys = Object.keys(bedDataWithoutOrgId || {});
  const isOccupancyOnlyUpdate =
    payloadKeys.length === 1 &&
    Object.prototype.hasOwnProperty.call(bedDataWithoutOrgId, "isOccupied");

  let finalPayload = bedDataWithoutOrgId;

  if (isOccupancyOnlyUpdate) {
    try {
      const existingRes = await apiClient.get(`${API_URL}/${id}`);
      const existingBed = existingRes?.data || {};

      finalPayload = {
        wardId: existingBed.wardId,
        bedNumber: existingBed.bedNumber,
        bedType: existingBed.bedType,
        bedPrice: existingBed.bedPrice,
        isOccupied: bedDataWithoutOrgId.isOccupied,
      };
    } catch (error) {
      console.warn(
        `[BedService] Failed to hydrate bed ${id} before occupancy update; continuing with partial payload.`,
        error
      );
    }
  }

  // apiClient will automatically add organizationId as query parameter via interceptor
  // (not in body, as Bed DTO doesn't accept it in request body)
  const response = await apiClient.put(`${API_URL}/${id}`, finalPayload);
  return response.data;
};

export default {
  addBed,
  getBedsByWard,
  updateBed,
  deleteBed,
} 